import { useEffect, useState, useCallback } from "react";
import {
  resolveUnitSystem,
  setUnitSystem as persistUnitSystem,
  type UnitSystem,
} from "@/utils/unitSettings";

/**
 * React hook for the active distance units.
 *
 * WHY A HOOK AT ALL. `fmtDistance()` reads the setting at call time, so a
 * component that formats a distance and never re-renders keeps showing the old
 * units after the driver changes them. Every route card on screen would be
 * stale until something else happened to re-render it — which is the kind of
 * bug that looks like "the setting does not work".
 *
 * Mirrors `useLocale` deliberately: same event pattern, same cross-tab storage
 * listener, same shape. Two settings that behave differently for no reason is
 * how a codebase gets hard to hold in your head.
 */
export function useUnitSystem(): [UnitSystem, (next: UnitSystem) => void] {
  const [system, setSystemState] = useState<UnitSystem>(() => resolveUnitSystem());

  useEffect(() => {
    const onChange = (e: Event) => {
      setSystemState((e as CustomEvent<UnitSystem>).detail ?? resolveUnitSystem());
    };
    // Cross-tab: another tab changed the choice.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "pleasant.units") setSystemState(resolveUnitSystem());
    };
    window.addEventListener("pleasant:unitschange", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pleasant:unitschange", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = useCallback((next: UnitSystem) => {
    persistUnitSystem(next);
    setSystemState(next);
  }, []);

  return [system, update];
}
